import React, { useState, useMemo, useEffect } from 'react'

import {
    Chip,
    Paper,
    useTheme,
    CircularProgress,
    IconButton,
    Tooltip,
    Button,
} from '@material-ui/core'
import { makeStyles, createStyles, Theme } from '@material-ui/core/styles'
import HomeIcon from '@material-ui/icons/Home'
import PersonIcon from '@material-ui/icons/Person'
import EventIcon from '@material-ui/icons/Event'
import FolderIcon from '@material-ui/icons/Folder'
import GetAppIcon from '@material-ui/icons/GetApp'

import { useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { Container, MessageDrawer } from '../../components'
import { WorkorderRow } from '../../components/WorkorderLib/WorkorderRow/WorkorderRow'
import { LeaseCard } from './LeaseCard'
import { InspectionCard } from './InspectionCard'
import {
    useRootInfrastructure,
    useAptConfig,
    useLease,
    useAreaStatusTag,
    useService,
    useCompany,
    useSchedule,
} from '../../hooks'
import { useUnitAreaInspection } from '../../hooks/useUnitAreaInspection'
import {
    FolderRes,
    getInventoryInspectionList,
    setInventoryInspectionList,
} from '../../store'
import { useAppDispatch } from '../../hooks'
import {
    Area,
    Lease,
    Unit,
    ModelMap,
    UnitInspection,
    InventoryInspection,
    WorkorderType,
    InspectionStatus,
    getInspectionStatusLabel,
    getIssueCount,
    getLeaseStatusString,
    getUiStatus,
    traverse,
} from '../../models'
import { toMMDDYYYY } from '../../helpers'
import { UnitInspectionDetailForm } from '../Inspection/UnitInspectionDetailForm'
import { useWorkorderApi } from '../../contexts/workorder/useWorkorderApi'
import { GetWorkOrderListRequest } from '../../contexts/workorder/workorder'
import { useUser, useMessageDrawer } from '../../hooks'
import { WorkorderResponse, RootState } from '../../store'
import GoBackModal from '../ScheduleHome/ViewComponents/GoBackModal'
import { typographyStyle } from '../../theme/typographyStyle'
import { MoveInspectionRow } from '../DamageHome/MoveInspectionContainer'
import { MoveInModal } from '../DamageHome/MoveInModal'
import { recordsUnitPath } from '../Records/paths'

interface UnitHistoriesProps {
    selectedAreaId: number | null
}

const eventTypeFilters = [
    { label: 'Lease', value: 'lease', color: '#46b083' },
    {
        label: 'Move In Inspections',
        value: 'move_in_inspection',
        color: '#E91E63',
    },
    { label: 'Inspections', value: 'inspection', color: '#0182F9' },
    { label: 'Service Order', value: 'service_order', color: '#F5A623' },
    { label: 'Action Item', value: 'action_item', color: '#7B68EE' },
]

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        container: {
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            minHeight: 0,
            overflow: 'hidden',
            backgroundColor: theme.palette.grey[50],
        },
        emptyContainer: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            backgroundColor: theme.palette.grey[50],
            padding: theme.spacing(4),
        },
        emptyIcon: {
            width: 80,
            height: 80,
            borderRadius: '50%',
            backgroundColor: theme.palette.grey[200],
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: theme.spacing(2),
        },
        emptyTitle: {
            ...(typographyStyle(theme.typography.h6) as Record<
                string,
                unknown
            >),
            color: theme.palette.text.secondary,
            marginBottom: theme.spacing(1),
        },
        emptySubtitle: {
            ...(typographyStyle(theme.typography.body2) as Record<
                string,
                unknown
            >),
            color: theme.palette.text.disabled,
        },
        unitInfoPanel: {
            backgroundColor: theme.palette.background.paper,
            padding: theme.spacing(3),
            borderRadius: 0,
            boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
            flexShrink: 0,
        },
        unitHeader: {
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            marginBottom: theme.spacing(3),
        },
        unitTitleSection: {
            display: 'flex',
            alignItems: 'center',
            gap: theme.spacing(2),
        },
        unitIcon: {
            width: 56,
            height: 56,
            backgroundColor: theme.palette.primary.main,
            borderRadius: theme.spacing(2),
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: `0 4px 12px ${theme.palette.primary.main}40`,
        },
        unitTitleContainer: {
            display: 'flex',
            flexDirection: 'column',
            gap: theme.spacing(0.5),
        },
        unitTitle: {
            ...(typographyStyle(theme.typography.h5) as Record<
                string,
                unknown
            >),
            fontWeight: 700,
            color: theme.palette.text.primary,
        },
        areaLabel: {
            ...(typographyStyle(theme.typography.body2) as Record<
                string,
                unknown
            >),
            color: theme.palette.text.secondary,
        },
        folderPath: {
            display: 'flex',
            alignItems: 'center',
            gap: theme.spacing(0.5),
            ...(typographyStyle(theme.typography.caption) as Record<
                string,
                unknown
            >),
            color: theme.palette.text.disabled,
            marginBottom: theme.spacing(0.25),
        },
        folderIcon: {
            fontSize: 14,
            color: theme.palette.text.disabled,
        },
        loadingContainer: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: theme.spacing(4),
        },
        actionButtons: {
            display: 'flex',
            alignItems: 'center',
            gap: theme.spacing(1),
        },
        viewProfileBtn: {
            textTransform: 'none',
            borderRadius: theme.spacing(1),
            padding: theme.spacing(1, 2.5),
            fontWeight: 600,
            boxShadow: 'none',
            '&:hover': {
                boxShadow: `0 2px 8px ${theme.palette.primary.main}40`,
            },
        },
        infoGrid: {
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: theme.spacing(2),
        },
        infoCard: {
            padding: theme.spacing(2),
            backgroundColor: theme.palette.grey[50],
            borderRadius: theme.spacing(1.5),
            display: 'flex',
            alignItems: 'flex-start',
            gap: theme.spacing(1.5),
        },
        infoIconWrapper: {
            width: 36,
            height: 36,
            borderRadius: theme.spacing(1),
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: theme.palette.common.white,
            boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
        },
        infoContent: {
            display: 'flex',
            flexDirection: 'column',
            gap: theme.spacing(0.25),
        },
        infoLabel: {
            ...(typographyStyle(theme.typography.caption) as Record<
                string,
                unknown
            >),
            color: theme.palette.text.secondary,
            fontWeight: 500,
        },
        infoValue: {
            ...(typographyStyle(theme.typography.body2) as Record<
                string,
                unknown
            >),
            fontWeight: 600,
            color: theme.palette.text.primary,
        },
        filterBar: {
            backgroundColor: theme.palette.background.paper,
            padding: theme.spacing(2, 3),
            borderTop: `1px solid ${theme.palette.divider}`,
            borderBottom: `1px solid ${theme.palette.divider}`,
            flexShrink: 0,
        },
        filterContainer: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
        },
        filterChips: {
            display: 'flex',
            alignItems: 'center',
            gap: theme.spacing(1),
        },
        filterChip: {
            borderRadius: theme.spacing(2.5),
            fontWeight: 500,
            fontSize: 13,
            height: 32,
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            border: `1px solid ${theme.palette.divider}`,
            backgroundColor: 'transparent',
            '&:hover': {
                backgroundColor: theme.palette.grey[50],
            },
        },
        filterChipActive: {
            backgroundColor: theme.palette.primary.main,
            color: theme.palette.common.white,
            border: `1px solid ${theme.palette.primary.main}`,
            '&:hover': {
                backgroundColor: theme.palette.primary.dark,
            },
        },
        filterDot: {
            width: 8,
            height: 8,
            borderRadius: '50%',
            marginRight: theme.spacing(0.5),
        },
        eventCount: {
            display: 'flex',
            alignItems: 'center',
            gap: theme.spacing(1),
            padding: theme.spacing(1, 2),
            backgroundColor: theme.palette.grey[100],
            borderRadius: theme.spacing(2),
            ...(typographyStyle(theme.typography.body2) as Record<
                string,
                unknown
            >),
            color: theme.palette.text.secondary,
            fontWeight: 500,
        },
        eventsContainer: {
            flex: 1,
            minHeight: 0,
            overflowY: 'auto',
            overflowX: 'hidden',
            padding: theme.spacing(3),
            '&::-webkit-scrollbar': {
                width: 6,
            },
            '&::-webkit-scrollbar-track': {
                backgroundColor: 'transparent',
            },
            '&::-webkit-scrollbar-thumb': {
                backgroundColor: theme.palette.grey[300],
                borderRadius: 3,
                '&:hover': {
                    backgroundColor: theme.palette.grey[400],
                },
            },
        },
        eventsHeader: {
            display: 'flex',
            alignItems: 'center',
            gap: theme.spacing(1),
            marginBottom: theme.spacing(2),
        },
        eventsTitle: {
            ...(typographyStyle(theme.typography.subtitle1) as Record<
                string,
                unknown
            >),
            fontWeight: 600,
            color: theme.palette.text.primary,
        },
        eventsList: {
            display: 'flex',
            flexDirection: 'column',
            gap: theme.spacing(2),
            position: 'relative',
            paddingLeft: theme.spacing(3.5),
            '&::before': {
                content: '""',
                position: 'absolute',
                left: 6,
                top: 0,
                bottom: 0,
                width: 2,
                backgroundColor: theme.palette.grey[200],
                borderRadius: 1,
            },
        },
        emptyState: {
            padding: theme.spacing(6),
            textAlign: 'center',
            borderRadius: theme.spacing(2),
            backgroundColor: theme.palette.background.paper,
            boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
        },
        emptyStateIcon: {
            width: 64,
            height: 64,
            borderRadius: '50%',
            backgroundColor: theme.palette.grey[100],
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto',
            marginBottom: theme.spacing(2),
        },
        emptyStateText: {
            ...(typographyStyle(theme.typography.body1) as Record<
                string,
                unknown
            >),
            color: theme.palette.text.secondary,
        },
    }),
)

export const UnitHistories = ({ selectedAreaId }: UnitHistoriesProps) => {
    const theme = useTheme()
    const classes = useStyles()
    const history = useHistory()
    const dispatch = useAppDispatch()
    const [eventFilter, setEventFilter] = useState('lease')
    const [selectedInspection, setSelectedInspection] = useState<
        UnitInspection | undefined
    >(undefined)
    const [serviceOrders, setServiceOrders] = useState<WorkorderResponse[]>([])
    const [actionItems, setActionItems] = useState<WorkorderResponse[]>([])
    const [serviceOrdersLoading, setServiceOrdersLoading] = useState(false)
    const [actionItemsLoading, setActionItemsLoading] = useState(false)
    const [messageDrawerOpen, setMessageDrawerOpen] = useState(false)
    const [messageDrawerTitle, setMessageDrawerTitle] = useState('Messages')
    const [openGoBackModal, setOpenGoBackModal] = useState<{
        open: boolean
        workorder: WorkorderResponse | null
    }>({ open: false, workorder: null })
    const [comparisonModalLease, setComparisonModalLease] = useState<
        Lease | undefined
    >(undefined)

    const { workspaceUser, userList, actions: userActions } = useUser()
    const { setChannel } = useMessageDrawer()
    const currentChannel = useSelector(
        (state: RootState) => state.messaging.currentChannel,
    )
    const {
        getWorkorderList,
        transitionWorkorder: apiTransitionWorkorder,
        createGoBack,
    } = useWorkorderApi()

    const { unitList, folderList, tree } = useRootInfrastructure(true)
    const {
        areaConfigList,
        getUnitConfigMap,
        getInventoryConfigMap,
        getAreaConfigMap,
    } = useAptConfig({
        areaConfigList: true,
        unitConfigList: true,
        inventoryConfigList: true,
    })
    const unitConfigMap = getUnitConfigMap()
    const inventoryConfigMap = getInventoryConfigMap()
    const areaConfigMap = getAreaConfigMap()
    const {
        leaseList,
        getLeaseList,
        isLoading: leasesLoading,
        createLeaseMoveInInvItems,
        addIssueToInspection,
        addImageToIssue,
        removeImageFromIssue,
        removeIssue,
    } = useLease()
    const { serviceList } = useService({ getServiceList: true, cleanUp: true })
    const { vendorList } = useCompany({ getVendorList: true, cleanUp: true })
    const { scheduleList, getScheduleList } = useSchedule()
    const { unitInspections, getUnitInspections } = useUnitAreaInspection()
    const [inspectionsLoading, setInspectionsLoading] = useState(false)

    useEffect(() => {
        userActions.getUserList({ params: { my_team: true } })
        getScheduleList({})
    }, [])

    // Get area status tags (needed for detail form)
    const { areaStatusTagMap } = useAreaStatusTag({
        cleanUp: true,
    })

    // Fetch leases when area changes
    useEffect(() => {
        if (selectedAreaId) {
            getLeaseList({
                params: {
                    area: selectedAreaId,
                },
            })
        }
    }, [selectedAreaId])

    // Sort leases by most recent first (by start_date)
    const sortedLeases = useMemo(() => {
        return [...leaseList].sort((a, b) => {
            const dateA = new Date(a.start_date).getTime()
            const dateB = new Date(b.start_date).getTime()
            return dateB - dateA // Most recent first
        })
    }, [leaseList])

    // Leases that have a tenant move-in inspection, sorted most recent first
    const moveInLeases = useMemo(() => {
        return sortedLeases.filter(
            (lease) => lease.tenant_move_in_inspection != null,
        )
    }, [sortedLeases])

    // Build a unit map keyed by unit id (used by MoveInspectionRow)
    const moveInUnitMap = useMemo(() => {
        const map: ModelMap<Unit> = {}
        if (tree.root) {
            traverse(tree.root, (folder) => {
                folder.units.forEach((u) => {
                    map[u.id] = u
                })
            })
        }
        return map
    }, [tree.version])

    // Create folder map
    const folderMap = useMemo(() => {
        const map: Record<number, FolderRes> = {}
        folderList?.forEach((folder) => {
            map[folder.id] = folder
        })
        return map
    }, [folderList])

    // Find the selected area and its parent unit
    const selectedData = useMemo(() => {
        if (!selectedAreaId || !unitList) return null

        for (const unit of unitList) {
            const area = unit.areas.find((a: Area) => a.id === selectedAreaId)
            if (area) {
                const areaConfig = areaConfigList?.find(
                    (config) => config.id === area.area_config,
                )
                const folder = folderMap[unit.folder_id]
                const folderPath = folder ? `${folder.path}${folder.name}` : ''
                return {
                    unit,
                    area,
                    areaConfig,
                    folderPath,
                }
            }
        }
        return null
    }, [selectedAreaId, unitList, areaConfigList, folderMap])

    // Fetch inspections when area changes or filter changes to inspection
    useEffect(() => {
        if (selectedAreaId && selectedData && eventFilter === 'inspection') {
            setInspectionsLoading(true)
            getUnitInspections({
                params: {
                    unit: selectedData.unit.id,
                    area: selectedAreaId,
                },
            }).finally(() => {
                setInspectionsLoading(false)
            })
        }
    }, [selectedAreaId, eventFilter, selectedData?.unit?.id])

    // Clear selected inspection when area changes
    useEffect(() => {
        setSelectedInspection(undefined)
    }, [selectedAreaId])

    // Fetch service orders when area changes or filter is service_order
    useEffect(() => {
        if (!selectedAreaId || eventFilter !== 'service_order') return
        setServiceOrdersLoading(true)
        const request: GetWorkOrderListRequest = {
            params: {
                workorder_type: WorkorderType.SERVICE_ORDER,
                area: selectedAreaId,
            },
        }
        getWorkorderList(request)
            .then((data) => setServiceOrders(Array.isArray(data) ? data : []))
            .catch(() => setServiceOrders([]))
            .finally(() => setServiceOrdersLoading(false))
    }, [selectedAreaId, eventFilter])

    // Fetch action items when area changes or filter is action_item
    useEffect(() => {
        if (!selectedAreaId || eventFilter !== 'action_item') return
        setActionItemsLoading(true)
        const request: GetWorkOrderListRequest = {
            params: {
                workorder_type: WorkorderType.ACTION_ITEM,
                area: selectedAreaId,
            },
        }
        getWorkorderList(request)
            .then((data) => setActionItems(Array.isArray(data) ? data : []))
            .catch(() => setActionItems([]))
            .finally(() => setActionItemsLoading(false))
    }, [selectedAreaId, eventFilter])

    // Fetch inventory inspections when an inspection is selected (needed for detail form)
    useEffect(() => {
        if (selectedInspection && selectedInspection.inspection?.id) {
            dispatch(
                getInventoryInspectionList({
                    params: {
                        inspection: selectedInspection.inspection.id,
                        units: `${selectedInspection.unit.id}`,
                    },
                }),
            )
        } else {
            dispatch(setInventoryInspectionList(undefined))
        }
    }, [selectedInspection])

    // Sort inspections by most recent first
    const sortedInspections = useMemo(() => {
        return [...unitInspections].sort((a, b) => {
            const dateA = new Date(a.start_date).getTime()
            const dateB = new Date(b.start_date).getTime()
            return dateB - dateA // Most recent first
        })
    }, [unitInspections])

    // Sort service orders and action items by end_date, most recent first
    const sortedServiceOrders = useMemo(() => {
        return [...serviceOrders].sort((a, b) => {
            const dateA = a.end_date ? new Date(a.end_date).getTime() : 0
            const dateB = b.end_date ? new Date(b.end_date).getTime() : 0
            return dateB - dateA
        })
    }, [serviceOrders])

    const sortedActionItems = useMemo(() => {
        return [...actionItems].sort((a, b) => {
            const dateA = a.end_date ? new Date(a.end_date).getTime() : 0
            const dateB = b.end_date ? new Date(b.end_date).getTime() : 0
            return dateB - dateA
        })
    }, [actionItems])

    // Filter function for inventory inspections (required by UnitInspectionDetailForm)
    const isInventoryInspectionInFilter = (
        invIns: InventoryInspection,
    ): boolean => {
        return true // Show all inventory inspections
    }

    const handleDownloadCsv = () => {
        if (!selectedData) return

        const {
            unit: csvUnit,
            area: csvArea,
            areaConfig: csvAreaConfig,
        } = selectedData

        // Wrap a value for CSV: convert to string, escape inner quotes, wrap in double quotes
        const csvCell = (value: unknown): string => {
            if (value === null || value === undefined) return '""'
            const str = String(value).replace(/"/g, '""')
            return `"${str}"`
        }

        const downloadCsv = (rows: string[], filenameSuffix: string) => {
            const csvContent = rows.join('\n')
            const blob = new Blob([csvContent], {
                type: 'text/csv;charset=utf-8;',
            })
            const url = window.URL.createObjectURL(blob)
            const a = document.createElement('a')
            const today = toMMDDYYYY(new Date()).replace(/\//g, '-')
            const sanitize = (value: string) =>
                value.replace(/[^a-z0-9_-]/gi, '_')
            const safeUnitName = sanitize(csvUnit?.name ?? 'unit')
            const safeAreaName = sanitize(csvAreaConfig?.name ?? 'area')
            const safeAreaLabel = sanitize(csvArea?.area_label ?? '')
            a.href = url
            a.download = `${safeUnitName}_${safeAreaName}_${safeAreaLabel}_${filenameSuffix}_${today}.csv`
            a.click()
            window.URL.revokeObjectURL(url)
        }

        const areaLabel = `${csvAreaConfig?.name ?? ''} (${csvArea.area_label})`

        if (eventFilter === 'lease') {
            const headers = [
                'Tenant Name',
                'Email',
                'Status',
                'Start Date',
                'End Date',
                'Unit',
                'Area',
            ]
            const rows = [
                headers.map(csvCell).join(','),
                ...sortedLeases.map((lease) =>
                    [
                        lease.user.name,
                        lease.user.email,
                        getLeaseStatusString(lease.status),
                        toMMDDYYYY(new Date(lease.start_date)),
                        toMMDDYYYY(new Date(lease.end_date)),
                        csvUnit.name,
                        areaLabel,
                    ]
                        .map(csvCell)
                        .join(','),
                ),
            ]
            downloadCsv(rows, 'leases')
            return
        }

        if (eventFilter === 'move_in_inspection') {
            // Render an HTML table and save it as .xls so the issues section
            // can be visually distinguished with background colors and bold text.
            // Excel and Google Sheets both open HTML-as-XLS and respect inline styles.
            const escapeHtml = (value: unknown): string => {
                if (value === null || value === undefined) return ''
                return String(value)
                    .replace(/&/g, '&amp;')
                    .replace(/</g, '&lt;')
                    .replace(/>/g, '&gt;')
                    .replace(/"/g, '&quot;')
            }

            const leaseHeaders = [
                'Tenant Name',
                'Email',
                'Lease Status',
                'Lease Start Date',
                'Lease End Date',
                'Inspection Status',
                'Issues Found',
                'Unit',
                'Area',
            ]

            const leaseHeaderStyle =
                'background-color:#1976d2;color:#ffffff;font-weight:bold;padding:6px 10px;border:1px solid #cccccc;text-align:left;'
            const leaseRowStyle =
                'padding:6px 10px;border:1px solid #cccccc;background-color:#ffffff;'
            const issueSectionHeaderStyle =
                'background-color:#fde7ea;color:#b71c1c;font-weight:bold;font-style:italic;padding:6px 10px;border:1px solid #f1b0b7;text-align:left;'
            const issueSubHeaderStyle =
                'background-color:#fff3cd;color:#5a4a00;font-weight:bold;padding:6px 10px;border:1px solid #f1b0b7;text-align:left;'
            const issueCellStyle =
                'background-color:#fff8e1;padding:6px 10px;border:1px solid #f1b0b7;color:#3e2723;'

            let html =
                '<html><head><meta charset="UTF-8"></head><body>' +
                '<table style="border-collapse:collapse;font-family:Arial,sans-serif;font-size:12px;">'

            html +=
                '<tr>' +
                leaseHeaders
                    .map(
                        (h) =>
                            `<th style="${leaseHeaderStyle}">${escapeHtml(
                                h,
                            )}</th>`,
                    )
                    .join('') +
                '</tr>'

            moveInLeases.forEach((lease) => {
                let displayStatus = getInspectionStatusLabel(
                    lease.tenant_move_in_inspection?.status,
                )
                if (
                    lease.tenant_move_in_inspection?.status ===
                    InspectionStatus.COMPLETE
                ) {
                    displayStatus = lease.tenant_move_in_inspection
                        .auto_completed
                        ? 'Auto Complete'
                        : 'Tenant Completed'
                }

                const leaseValues = [
                    lease.user.name,
                    lease.user.email,
                    getLeaseStatusString(lease.status),
                    toMMDDYYYY(new Date(lease.start_date)),
                    toMMDDYYYY(new Date(lease.end_date)),
                    displayStatus,
                    getIssueCount(lease),
                    csvUnit.name,
                    areaLabel,
                ]
                html +=
                    '<tr>' +
                    leaseValues
                        .map(
                            (v) =>
                                `<td style="${leaseRowStyle}">${escapeHtml(
                                    v,
                                )}</td>`,
                        )
                        .join('') +
                    '</tr>'

                const inventoryInspections =
                    lease.tenant_move_in_inspection?.inventory_inspection ?? []

                const issueCount = inventoryInspections.reduce(
                    (sum, invIns) => sum + (invIns.issue?.length ?? 0),
                    0,
                )

                if (issueCount > 0) {
                    html +=
                        '<tr>' +
                        `<td style="${issueSectionHeaderStyle}" colspan="${leaseHeaders.length}">` +
                        `&#9658; Issues (${issueCount})` +
                        '</td>' +
                        '</tr>'

                    const subHeaders = ['Inventory Item', 'Description']
                    html +=
                        '<tr>' +
                        // Pad two empty cells so issue columns visually indent past the lease key columns
                        `<td style="${issueSubHeaderStyle}"></td><td style="${issueSubHeaderStyle}"></td>` +
                        subHeaders
                            .map(
                                (h) =>
                                    `<td style="${issueSubHeaderStyle}">${escapeHtml(
                                        h,
                                    )}</td>`,
                            )
                            .join('') +
                        // Pad remaining columns to match leaseHeaders length
                        Array(leaseHeaders.length - 2 - subHeaders.length)
                            .fill(`<td style="${issueSubHeaderStyle}"></td>`)
                            .join('') +
                        '</tr>'

                    inventoryInspections.forEach((invIns) => {
                        const inventoryName =
                            inventoryConfigMap[invIns.inventory_config]?.name ??
                            ''
                        invIns.issue?.forEach((issue) => {
                            const issueValues = [
                                inventoryName,
                                issue.description ?? '',
                            ]
                            html +=
                                '<tr>' +
                                `<td style="${issueCellStyle}"></td><td style="${issueCellStyle}"></td>` +
                                issueValues
                                    .map(
                                        (v) =>
                                            `<td style="${issueCellStyle}">${escapeHtml(
                                                v,
                                            )}</td>`,
                                    )
                                    .join('') +
                                Array(
                                    leaseHeaders.length -
                                        2 -
                                        issueValues.length,
                                )
                                    .fill(`<td style="${issueCellStyle}"></td>`)
                                    .join('') +
                                '</tr>'
                        })
                    })
                }
            })

            html += '</table></body></html>'

            const blob = new Blob([html], {
                type: 'application/vnd.ms-excel;charset=utf-8;',
            })
            const url = window.URL.createObjectURL(blob)
            const a = document.createElement('a')
            const today = toMMDDYYYY(new Date()).replace(/\//g, '-')
            const sanitize = (value: string) =>
                value.replace(/[^a-z0-9_-]/gi, '_')
            const safeUnitName = sanitize(csvUnit?.name ?? 'unit')
            const safeAreaName = sanitize(csvAreaConfig?.name ?? 'area')
            const safeAreaLabel = sanitize(csvArea?.area_label ?? '')
            a.href = url
            a.download = `${safeUnitName}_${safeAreaName}_${safeAreaLabel}_move_in_inspections_${today}.xls`
            a.click()
            window.URL.revokeObjectURL(url)
            return
        }

        if (eventFilter === 'inspection') {
            const headers = [
                'Inspection Name',
                'Folder Path',
                'Unit',
                'Unit Config',
                'Assigned To',
                'Start Date',
                'End Date',
                'Total Cost',
                'Total Flags',
                'Total Handled',
            ]
            const rows = [
                headers.map(csvCell).join(','),
                ...sortedInspections.map((ins) => {
                    let cost = 0
                    let flags = 0
                    let handled = 0
                    ins.area_inspections.forEach((areaIns) => {
                        cost += areaIns.cost
                        flags += areaIns.flagged_count
                        handled += areaIns.handled_count
                    })
                    const folder = `${ins.folder?.path ?? ''}${
                        ins.folder?.name ?? ''
                    }`
                    const unitConfigName =
                        unitConfigMap[ins.unit.unit_config]?.name ?? ''
                    return [
                        ins.inspection?.name ?? '',
                        folder,
                        ins.unit.name,
                        unitConfigName,
                        ins.assigned_to?.name ?? 'My Team',
                        toMMDDYYYY(new Date(ins.start_date)),
                        toMMDDYYYY(new Date(ins.end_date)),
                        cost.toFixed(2),
                        flags,
                        handled,
                    ]
                        .map(csvCell)
                        .join(',')
                }),
            ]
            downloadCsv(rows, 'inspections')
            return
        }

        if (eventFilter === 'service_order' || eventFilter === 'action_item') {
            const isServiceOrder = eventFilter === 'service_order'
            const list = isServiceOrder
                ? sortedServiceOrders
                : sortedActionItems
            const headers = [
                'Title',
                'Service',
                'Vendor',
                'Status',
                'Start Date',
                'End Date',
                'Price',
                'Folder Path',
                'Unit',
                ...(isServiceOrder ? ['Schedule'] : []),
            ]
            const rows = [
                headers.map(csvCell).join(','),
                ...list.map((wo) => {
                    const price =
                        wo.invoiced_price ?? wo.custom_price ?? wo.fv_price ?? 0
                    return [
                        wo.title ?? '',
                        wo.service_name ?? '',
                        wo.vendor_name ?? '',
                        getUiStatus(wo.status),
                        wo.start_date
                            ? toMMDDYYYY(new Date(wo.start_date))
                            : '',
                        wo.end_date ? toMMDDYYYY(new Date(wo.end_date)) : '',
                        Number(price).toFixed(2),
                        `${wo.path ?? ''}${wo.folder_name ?? ''}`,
                        wo.unit_name ?? '',
                        ...(isServiceOrder ? [wo.schedule_name ?? ''] : []),
                    ]
                        .map(csvCell)
                        .join(',')
                }),
            ]
            downloadCsv(
                rows,
                isServiceOrder ? 'service_orders' : 'action_items',
            )
            return
        }
    }

    // Show empty state if no area is selected
    if (!selectedAreaId || !selectedData) {
        return (
            <div className={classes.emptyContainer}>
                <div className={classes.emptyIcon}>
                    <HomeIcon
                        style={{
                            fontSize: 36,
                            color: theme.palette.text.disabled,
                        }}
                    />
                </div>
                <div className={classes.emptyTitle}>No Space Selected</div>
                <div className={classes.emptySubtitle}>
                    Select a bed space from the sidebar to view its history
                </div>
            </div>
        )
    }

    const { unit, area, areaConfig, folderPath } = selectedData

    return (
        <div className={classes.container}>
            {/* Unit Info Panel */}
            <div className={classes.unitInfoPanel}>
                <div className={classes.unitHeader}>
                    <div className={classes.unitTitleSection}>
                        <div className={classes.unitIcon}>
                            <HomeIcon
                                style={{
                                    color: theme.palette.common.white,
                                    fontSize: 28,
                                }}
                            />
                        </div>
                        <div className={classes.unitTitleContainer}>
                            {folderPath && (
                                <div className={classes.folderPath}>
                                    <FolderIcon
                                        className={classes.folderIcon}
                                    />
                                    {folderPath}
                                </div>
                            )}
                            <span className={classes.unitTitle}>
                                Unit {unit.name}
                            </span>
                            <span className={classes.areaLabel}>
                                {area.area_config_name} {area.area_label}
                            </span>
                        </div>
                    </div>
                    <div className={classes.actionButtons}>
                        <Button
                            className={classes.viewProfileBtn}
                            variant="outlined"
                            color="primary"
                            onClick={() =>
                                history.push(recordsUnitPath(unit.id))
                            }
                        >
                            Open in Records
                        </Button>
                    </div>
                </div>
            </div>

            {/* Filter Bar */}
            <div className={classes.filterBar}>
                <div className={classes.filterContainer}>
                    <div className={classes.filterChips}>
                        {eventTypeFilters.map((filter) => (
                            <Chip
                                key={filter.value}
                                label={
                                    <Container alignItems="center">
                                        {filter.color && (
                                            <span
                                                className={classes.filterDot}
                                                style={{
                                                    backgroundColor:
                                                        eventFilter ===
                                                        filter.value
                                                            ? theme.palette
                                                                  .common.white
                                                            : filter.color,
                                                }}
                                            />
                                        )}
                                        {filter.label}
                                    </Container>
                                }
                                onClick={() => setEventFilter(filter.value)}
                                className={`${classes.filterChip} ${
                                    eventFilter === filter.value
                                        ? classes.filterChipActive
                                        : ''
                                }`}
                            />
                        ))}
                    </div>
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: theme.spacing(1),
                        }}
                    >
                        <div className={classes.eventCount}>
                            <EventIcon style={{ fontSize: 16 }} />
                            {eventFilter === 'lease'
                                ? sortedLeases.length
                                : eventFilter === 'move_in_inspection'
                                ? moveInLeases.length
                                : eventFilter === 'inspection'
                                ? sortedInspections.length
                                : eventFilter === 'service_order'
                                ? sortedServiceOrders.length
                                : sortedActionItems.length}{' '}
                            item
                            {(eventFilter === 'lease'
                                ? sortedLeases.length
                                : eventFilter === 'move_in_inspection'
                                ? moveInLeases.length
                                : eventFilter === 'inspection'
                                ? sortedInspections.length
                                : eventFilter === 'service_order'
                                ? sortedServiceOrders.length
                                : sortedActionItems.length) !== 1
                                ? 's'
                                : ''}
                        </div>
                        <Tooltip
                            title={
                                eventFilter === 'move_in_inspection'
                                    ? 'Download Report (XLS)'
                                    : 'Download CSV'
                            }
                        >
                            <span>
                                <IconButton
                                    size="small"
                                    onClick={handleDownloadCsv}
                                    disabled={
                                        (eventFilter === 'lease' &&
                                            sortedLeases.length === 0) ||
                                        (eventFilter === 'move_in_inspection' &&
                                            moveInLeases.length === 0) ||
                                        (eventFilter === 'inspection' &&
                                            sortedInspections.length === 0) ||
                                        (eventFilter === 'service_order' &&
                                            sortedServiceOrders.length === 0) ||
                                        (eventFilter === 'action_item' &&
                                            sortedActionItems.length === 0)
                                    }
                                    aria-label="Download Report"
                                >
                                    <GetAppIcon />
                                </IconButton>
                            </span>
                        </Tooltip>
                    </div>
                </div>
            </div>

            {/* Activity Timeline */}
            <div className={classes.eventsContainer}>
                <div className={classes.eventsHeader}>
                    <EventIcon
                        style={{
                            color: theme.palette.text.secondary,
                            fontSize: 20,
                        }}
                    />
                    <span className={classes.eventsTitle}>
                        Activity Timeline
                    </span>
                </div>

                {leasesLoading && eventFilter === 'lease' ? (
                    <div className={classes.loadingContainer}>
                        <CircularProgress size={28} />
                    </div>
                ) : eventFilter === 'lease' ? (
                    <div className={classes.eventsList}>
                        {sortedLeases.length > 0 ? (
                            sortedLeases.map((lease) => (
                                <LeaseCard key={lease.id} lease={lease} />
                            ))
                        ) : (
                            <Paper className={classes.emptyState} elevation={0}>
                                <div className={classes.emptyStateIcon}>
                                    <PersonIcon
                                        style={{
                                            fontSize: 28,
                                            color: theme.palette.text.disabled,
                                        }}
                                    />
                                </div>
                                <div className={classes.emptyStateText}>
                                    No leases found for this area.
                                </div>
                            </Paper>
                        )}
                    </div>
                ) : leasesLoading && eventFilter === 'move_in_inspection' ? (
                    <div className={classes.loadingContainer}>
                        <CircularProgress size={28} />
                    </div>
                ) : eventFilter === 'move_in_inspection' ? (
                    <div className={classes.eventsList}>
                        {moveInLeases.length > 0 ? (
                            moveInLeases.map((lease) => (
                                <MoveInspectionRow
                                    key={`MOVE_IN_${lease.id}`}
                                    lease={lease}
                                    unitMap={moveInUnitMap}
                                    areaConfigMap={areaConfigMap}
                                    onClickMoveInInspection={(clickedLease) => {
                                        if (
                                            clickedLease
                                                .tenant_move_in_inspection
                                                ?.inventory_inspection
                                                ?.length === 0
                                        ) {
                                            createLeaseMoveInInvItems(
                                                clickedLease,
                                            ).then((updatedLease) => {
                                                setComparisonModalLease(
                                                    updatedLease,
                                                )
                                            })
                                        } else {
                                            setComparisonModalLease(
                                                clickedLease,
                                            )
                                        }
                                    }}
                                />
                            ))
                        ) : (
                            <Paper className={classes.emptyState} elevation={0}>
                                <div className={classes.emptyStateIcon}>
                                    <EventIcon
                                        style={{
                                            fontSize: 28,
                                            color: theme.palette.text.disabled,
                                        }}
                                    />
                                </div>
                                <div className={classes.emptyStateText}>
                                    No move in inspections found for this area.
                                </div>
                            </Paper>
                        )}
                    </div>
                ) : inspectionsLoading ? (
                    <div className={classes.loadingContainer}>
                        <CircularProgress size={28} />
                    </div>
                ) : eventFilter === 'inspection' ? (
                    <div className={classes.eventsList}>
                        {sortedInspections.length > 0 ? (
                            sortedInspections.map((inspection) => (
                                <InspectionCard
                                    key={inspection.id}
                                    unitInspection={inspection}
                                    unitConfig={
                                        unitConfigMap[
                                            inspection.unit.unit_config
                                        ]
                                    }
                                    selected={
                                        selectedInspection?.id === inspection.id
                                    }
                                    onClick={() =>
                                        setSelectedInspection(inspection)
                                    }
                                />
                            ))
                        ) : (
                            <Paper className={classes.emptyState} elevation={0}>
                                <div className={classes.emptyStateIcon}>
                                    <EventIcon
                                        style={{
                                            fontSize: 28,
                                            color: theme.palette.text.disabled,
                                        }}
                                    />
                                </div>
                                <div className={classes.emptyStateText}>
                                    No inspections found for this area.
                                </div>
                            </Paper>
                        )}
                    </div>
                ) : serviceOrdersLoading && eventFilter === 'service_order' ? (
                    <div className={classes.loadingContainer}>
                        <CircularProgress size={28} />
                    </div>
                ) : eventFilter === 'service_order' ? (
                    <div className={classes.eventsList}>
                        {sortedServiceOrders.length > 0 ? (
                            sortedServiceOrders.map((wo) => (
                                <WorkorderRow
                                    key={wo.id}
                                    workorder={wo}
                                    user={workspaceUser}
                                    canSeeUnitNotes
                                    hideCameraButton
                                    isSelectedWorkorder={
                                        wo.channel === currentChannel?.id ||
                                        wo.unit_channel_id ===
                                            currentChannel?.id
                                    }
                                    openMessageDrawer={() => {
                                        setChannel(wo.channel)
                                        setMessageDrawerTitle('Messages')
                                        setMessageDrawerOpen(true)
                                    }}
                                    openUnitNotes={() => {
                                        if (wo.unit_channel_id) {
                                            setChannel(wo.unit_channel_id)
                                            setMessageDrawerTitle('Unit Notes')
                                            setMessageDrawerOpen(true)
                                        }
                                    }}
                                    transitionWorkorder={(workorder, status) =>
                                        apiTransitionWorkorder(
                                            workorder,
                                            status,
                                        ).then((updated) => {
                                            setServiceOrders((prev) =>
                                                prev.map((w) =>
                                                    w.id === updated.id
                                                        ? updated
                                                        : w,
                                                ),
                                            )
                                        })
                                    }
                                    onClickWorkorder={() => {}}
                                    updateWorkorderState={(updated) => {
                                        setServiceOrders((prev) =>
                                            prev.map((w) =>
                                                w.id === updated.id
                                                    ? updated
                                                    : w,
                                            ),
                                        )
                                    }}
                                    onClickGoBack={() =>
                                        setOpenGoBackModal({
                                            open: true,
                                            workorder: wo,
                                        })
                                    }
                                    style={{
                                        width: '100%',
                                        borderRadius: theme.spacing(1),
                                        boxShadow: theme.shadows[1],
                                    }}
                                />
                            ))
                        ) : (
                            <Paper className={classes.emptyState} elevation={0}>
                                <div className={classes.emptyStateIcon}>
                                    <EventIcon
                                        style={{
                                            fontSize: 28,
                                            color: theme.palette.text.disabled,
                                        }}
                                    />
                                </div>
                                <div className={classes.emptyStateText}>
                                    No service orders found for this area.
                                </div>
                            </Paper>
                        )}
                    </div>
                ) : actionItemsLoading && eventFilter === 'action_item' ? (
                    <div className={classes.loadingContainer}>
                        <CircularProgress size={28} />
                    </div>
                ) : eventFilter === 'action_item' ? (
                    <div className={classes.eventsList}>
                        {sortedActionItems.length > 0 ? (
                            sortedActionItems.map((wo) => (
                                <WorkorderRow
                                    key={wo.id}
                                    workorder={wo}
                                    user={workspaceUser}
                                    canSeeUnitNotes
                                    hideCameraButton
                                    isSelectedWorkorder={
                                        wo.channel === currentChannel?.id ||
                                        wo.unit_channel_id ===
                                            currentChannel?.id
                                    }
                                    openMessageDrawer={() => {
                                        setChannel(wo.channel)
                                        setMessageDrawerTitle('Messages')
                                        setMessageDrawerOpen(true)
                                    }}
                                    openUnitNotes={() => {
                                        if (wo.unit_channel_id) {
                                            setChannel(wo.unit_channel_id)
                                            setMessageDrawerTitle('Unit Notes')
                                            setMessageDrawerOpen(true)
                                        }
                                    }}
                                    transitionWorkorder={(workorder, status) =>
                                        apiTransitionWorkorder(
                                            workorder,
                                            status,
                                        ).then((updated) => {
                                            setActionItems((prev) =>
                                                prev.map((w) =>
                                                    w.id === updated.id
                                                        ? updated
                                                        : w,
                                                ),
                                            )
                                        })
                                    }
                                    onClickWorkorder={() => {}}
                                    updateWorkorderState={(updated) => {
                                        setActionItems((prev) =>
                                            prev.map((w) =>
                                                w.id === updated.id
                                                    ? updated
                                                    : w,
                                            ),
                                        )
                                    }}
                                    onClickGoBack={() =>
                                        setOpenGoBackModal({
                                            open: true,
                                            workorder: wo,
                                        })
                                    }
                                    style={{
                                        width: '100%',
                                        borderRadius: theme.spacing(1),
                                        boxShadow: theme.shadows[1],
                                    }}
                                />
                            ))
                        ) : (
                            <Paper className={classes.emptyState} elevation={0}>
                                <div className={classes.emptyStateIcon}>
                                    <EventIcon
                                        style={{
                                            fontSize: 28,
                                            color: theme.palette.text.disabled,
                                        }}
                                    />
                                </div>
                                <div className={classes.emptyStateText}>
                                    No action items found for this area.
                                </div>
                            </Paper>
                        )}
                    </div>
                ) : null}
            </div>

            {/* Unit Inspection Detail Drawer */}
            <UnitInspectionDetailForm
                unitConfigMap={unitConfigMap}
                inventoryConfigMap={inventoryConfigMap}
                unitInspection={selectedInspection}
                areaStatusMap={areaStatusTagMap}
                open={selectedInspection !== undefined}
                isInventoryInspectionInFilter={isInventoryInspectionInFilter}
                onClose={() => setSelectedInspection(undefined)}
                setUnitInspection={setSelectedInspection}
            />

            {/* Move In Inspection Modal */}
            <MoveInModal
                open={comparisonModalLease !== undefined}
                handleClose={() => setComparisonModalLease(undefined)}
                lease={comparisonModalLease}
                unitMap={moveInUnitMap}
                areaConfigMap={areaConfigMap}
                invConfigMap={inventoryConfigMap}
                serviceList={serviceList}
                vendorList={vendorList}
                userList={userList}
                scheduleList={scheduleList}
                onLeaseUpdate={(updatedLease) => {
                    if (comparisonModalLease?.id === updatedLease.id) {
                        setComparisonModalLease(updatedLease)
                    }
                }}
                addIssueToInspection={addIssueToInspection}
                addImageToIssue={addImageToIssue}
                removeImageFromIssue={removeImageFromIssue}
                removeIssue={removeIssue}
            />

            {/* Message / Unit Notes Drawer */}
            <MessageDrawer
                open={messageDrawerOpen}
                title={messageDrawerTitle}
                onClose={() => setMessageDrawerOpen(false)}
                onCreateMessage={() => {
                    const wo =
                        serviceOrders.find(
                            (w) =>
                                w.channel === currentChannel?.id ||
                                w.unit_channel_id === currentChannel?.id,
                        ) ||
                        actionItems.find(
                            (w) =>
                                w.channel === currentChannel?.id ||
                                w.unit_channel_id === currentChannel?.id,
                        )
                    if (wo) {
                        const updated = {
                            ...wo,
                            message_count: (wo.message_count ?? 0) + 1,
                        }
                        if (serviceOrders.some((w) => w.id === wo.id)) {
                            setServiceOrders((prev) =>
                                prev.map((w) =>
                                    w.id === updated.id ? updated : w,
                                ),
                            )
                        } else {
                            setActionItems((prev) =>
                                prev.map((w) =>
                                    w.id === updated.id ? updated : w,
                                ),
                            )
                        }
                    }
                }}
            />

            {/* Go Back Modal */}
            <GoBackModal
                open={openGoBackModal.open}
                workorder={openGoBackModal.workorder}
                onClose={() =>
                    setOpenGoBackModal({ open: false, workorder: null })
                }
                onSubmit={(workorderId, formData) => {
                    createGoBack(workorderId, formData)
                        .then((updatedWorkorder) => {
                            if (
                                serviceOrders.some(
                                    (w) => w.id === updatedWorkorder.id,
                                )
                            ) {
                                setServiceOrders((prev) =>
                                    prev.map((w) =>
                                        w.id === updatedWorkorder.id
                                            ? updatedWorkorder
                                            : w,
                                    ),
                                )
                            } else {
                                setActionItems((prev) =>
                                    prev.map((w) =>
                                        w.id === updatedWorkorder.id
                                            ? updatedWorkorder
                                            : w,
                                    ),
                                )
                            }
                        })
                        .catch((error) => {
                            console.error('Error creating go back:', error)
                        })
                }}
            />
        </div>
    )
}
